import React from "react";
import { motion } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";

const ease = [0.16, 1, 0.3, 1];
const ABOUT_IMG =
  "https://images.unsplash.com/photo-1582057811341-a22d524c6a4c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxhcmNoaXRlY3QlMjB3b3Jrc3BhY2UlMjBibHVlcHJpbnR8ZW58MHx8fHwxNzc2NzE5Mjk0fDA&ixlib=rb-4.1.0&q=85";

export default function About() {
  const { t } = useLang();

  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-24 md:py-36 bg-[#F7F7F9]"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease }}
          className="flex items-center gap-3 mb-16 md:mb-24"
        >
          <span className="w-8 h-px bg-[#0A0A0A]" />
          <span className="font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase">
            {t.about.overline} / 01
          </span>
        </motion.div>

        <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
          {/* Image col */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, ease }}
            className="col-span-12 md:col-span-5 md:sticky md:top-28"
          >
            <div className="aspect-[4/5] overflow-hidden bg-[#EFEFEF] group">
              <img
                src={ABOUT_IMG}
                alt="Architect workspace"
                className="laka-photo w-full h-full object-cover group-hover:scale-[1.04]"
              />
            </div>
            <div className="mt-4 flex items-center justify-between font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C]">
              <span>Karim Kerkoub</span>
              <span>Studio — Strasbourg</span>
            </div>
          </motion.div>

          {/* Text col */}
          <div className="col-span-12 md:col-span-7 md:pl-8">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.9, ease }}
              className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight font-medium leading-[1.05] text-[#0A0A0A] max-w-[22ch] mb-12"
            >
              {t.about.title}
            </motion.h2>

            <div className="space-y-6 max-w-[58ch] text-[#0A0A0A]">
              {[t.about.p1, t.about.p2, t.about.p3].map((p, i) => (
                <motion.p
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.8, ease, delay: i * 0.1 }}
                  className="text-base md:text-lg font-light leading-relaxed"
                >
                  {p}
                </motion.p>
              ))}
            </div>

            {/* Stats */}
            <div className="mt-16 grid grid-cols-3 gap-4 md:gap-8 border-t border-[#E5E5E5] pt-10">
              {t.about.stats.map((s, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, ease, delay: i * 0.12 }}
                >
                  <div className="font-display text-4xl md:text-5xl tracking-tighter font-medium leading-none">
                    {s.k}
                  </div>
                  <div className="mt-3 font-mono text-[10px] md:text-xs tracking-[0.2em] uppercase text-[#5C5C5C]">
                    {s.v}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
